<script setup lang="ts">
import { ref, watch, defineProps, computed } from 'vue';
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import Alert from '@/components/ui/Alert.vue';
import InputPrice from '@/components/ui/InputPrice.vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { Button } from "@/shadcn/components/ui/button";
const { t: $t } = useI18n();

const props = defineProps({
  showModal: {
    type: Boolean,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  equipmentId: {
    type: Number,
    default: null
  },
  maintenanceRecord: {
    type: Object,
    default: null
  },
  maintenanceTypes: {
    type: Array,
    default: () => [],
  },
  maintenanceStatuses: {
    type: Array,
    default: () => [],
  },
  healthStates: {
    type: Array,
    default: () => [],
  },
  currencies: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'saved']);

const errors = ref<string[]>([]);

const formData = ref({
  equipment_id: props.equipmentId,
  type: '',
  subtype: '',
  status: 'planned',
  outcome_state: null,
  started_at: '',
  completed_at: '',
  next_check_at: '',
  labor_minutes: null,
  cost_cents: null,
  currency: 'EUR',
  notes: ''
});

const state = ref<'initializing' | 'saving' | 'idle'>('idle');

const editionMode = computed(() => props.isEditing);

// Format datetime for input field (YYYY-MM-DDTHH:mm)
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  return dayjs(dateString).format('YYYY-MM-DDTHH:mm');
};

// Load existing record data when editing
watch(() => props.showModal, (isOpen) => {
  if (isOpen) {
    errors.value = [];
    if (props.maintenanceRecord) {
      formData.value = {
        equipment_id: props.maintenanceRecord.equipment_id,
        type: props.maintenanceRecord.type?.value || null,
        subtype: props.maintenanceRecord.subtype || null,
        status: props.maintenanceRecord.status?.value || null,
        outcome_state: props.maintenanceRecord.outcome_state?.value || null,
        started_at: formatDateForInput(props.maintenanceRecord.started_at),
        completed_at: formatDateForInput(props.maintenanceRecord.completed_at),
        next_check_at: formatDateForInput(props.maintenanceRecord.next_check_at),
        labor_minutes: props.maintenanceRecord.labor_minutes || null,
        cost_cents: props.maintenanceRecord.cost_cents || null,
        currency: props.maintenanceRecord.currency || 'EUR',
        notes: props.maintenanceRecord.notes || ''
      };
    } else {
      // Creating new record
      formData.value = {
        equipment_id: props.equipmentId,
        type: '',
        subtype: '',
        status: 'planned',
        outcome_state: null,
        started_at: '',
        completed_at: '',
        next_check_at: '',
        labor_minutes: null,
        cost_cents: null,
        currency: 'EUR',
        notes: ''
      };
    }
  }
}, { immediate: true });

watch(() => formData.value.status, (newStatus) => {
  if (newStatus === "in_progress" && !formData.value.started_at) {
    formData.value.started_at = dayjs().format("YYYY-MM-DDTHH:mm");
  }

  if (newStatus === "done" && !formData.value.completed_at) {
    formData.value.completed_at = dayjs().format("YYYY-MM-DDTHH:mm");
  }
});
const submitForm = async () => {
  errors.value = [];
  state.value = 'saving';

  try {
    if (props.maintenanceRecord) {
      await axios.put(
        `/backoffice/api/maintenance-records/${props.maintenanceRecord.id}`,
        formData.value
      );
    } else {
      await axios.post('/backoffice/api/maintenance-records', formData.value);
    }

    state.value = 'idle';
    emit('saved');
  } catch (error: any) {
    state.value = 'idle';
    if (error.response?.data?.errors) {
      errors.value = Object.values(error.response.data.errors).flat() as string[];
    } else if (error.response?.data?.message) {
      errors.value = [error.response.data.message];
    } else {
      errors.value = [$t("An error occurred while saving the maintenance record")];
    }
  }
};

const closeModal = () => {
  emit('close');
};

const onDialogUpdate = (isOpen: boolean) => {
  if (!isOpen) {
    closeModal();
  }
};
</script>
<template>
  <Dialog :open="showModal" @update:open="onDialogUpdate">
    <DialogScrollContent class="tw:max-w-2xl">
      <form method="post" @submit.prevent="submitForm">
        <DialogHeader>
          <DialogTitle>
            {{ maintenanceRecord ? $t('Edit maintenance record') : $t('Create maintenance record') }}
          </DialogTitle>
        </DialogHeader>

        <div v-if="state === 'initializing'" class="tw:p-4 tw:text-center">
          {{ $t('loading…') }}
        </div>

        <fieldset v-else :disabled="state === 'saving' || !editionMode" class="tw:space-y-4">
          <!-- Type -->
          <div class="form-group">
            <label class="control-label">{{ $t('Type') }}<small class="text-danger">*</small></label>
            <select
              v-model="formData.type"
              class="form-control"
              required
            >
              <option value="" disabled>{{ $t('Select a type') }}</option>
              <option
                v-for="(type) in maintenanceTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
          </div>

          <!-- Subtype -->
          <div class="form-group">
            <label class="control-label">{{ $t('Subtype') }}</label>
            <input
              v-model="formData.subtype"
              type="text"
              class="form-control"
              :placeholder="$t('Short description of the intervention')"
            />
          </div>

          <!-- Status -->
          <div class="form-group">
            <label class="control-label">{{ $t('Status') }}</label>
            <select
              v-model="formData.status"
              class="form-control"
              required
            >
              <option value="" disabled>{{ $t('Select a status') }}</option>
              <option
                v-for="(status) in maintenanceStatuses"
                :key="status.value"
                :value="status.value"
              >
                {{ status.label }}
              </option>
            </select>
          </div>

          <!-- Dates -->
            <div class="form-group">
              <label class="control-label">{{ $t('Started at') }}</label>
              <input
                v-model="formData.started_at"
                type="datetime-local"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label class="control-label">{{ $t('Completed at') }}</label>
              <input
                v-model="formData.completed_at"
                type="datetime-local"
                class="form-control"
              />
            </div>

          <!-- Outcome State -->
          <div class="form-group">
            <label class="control-label">{{ $t('Outcome state') }}</label>
            <select
              v-model="formData.outcome_state"
              class="form-control"
              :required="!!formData.completed_at"
            >
              <option value="" disabled>{{ $t('Select a condition') }}</option>
              <option
                v-for="(state) in healthStates"
                :key="state.value"
                :value="state.value"
              >
                {{ state.label }}
              </option>
            </select>
          </div>

          <!-- Labor and Cost -->
            <div class="form-group">
              <label class="control-label">{{ $t('Time spent (minutes)') }}</label>
              <input
                v-model.number="formData.labor_minutes"
                type="number"
                min="0"
                class="form-control"
                :placeholder="$t('Duration in minutes')"
              />
            </div>

          <div class="tw:grid tw:grid-cols-2 tw:gap-4">
            <div class="form-group">
              <label class="control-label">{{ $t('Cost') }}</label>
              <InputPrice
                v-model="formData.cost_cents"
                :currency="formData.currency"/>
            </div>

            <div class="form-group">
              <label class="control-label">{{ $t('Currency') }}</label>
              <select
                v-model="formData.currency"
                class="form-control"
              >
                <option value="" disabled>{{ $t('Select a currency') }}</option>
                <option
                  v-for="(currency) in currencies"
                  :key="currency.value"
                  :value="currency.value"
                >
                  {{ currency.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Notes -->
          <div class="form-group">
            <label class="control-label">{{ $t('Notes') }}</label>
            <textarea
              v-model="formData.notes"
              class="form-control"
              rows="4"
              :placeholder="$t('Additional details about the maintenance...')"
            ></textarea>
          </div>

          <div class="form-group">
            <label class="control-label">{{ $t('Next check at') }}</label>
            <input
              v-model="formData.next_check_at"
              type="datetime-local"
              class="form-control"
            />
          </div>

        </fieldset>

        <Alert v-if="errors.length > 0" variant="danger" class="tw:mt-4">
          <ul class="tw:list-disc tw:list-inside tw:m-0">
            <li v-for="(error, index) in errors" :key="index">
              {{ error }}
            </li>
          </ul>
        </Alert>

        <DialogFooter v-if="editionMode" class="tw:mt-6">
          <Button
            type="button"
            variant="secondary"
            @click="closeModal"
            :disabled="state === 'saving'"
          >
            {{ $t('Cancel') }}
          </Button>
          <Button
            type="submit"
            :disabled="state === 'saving'"
          >
            {{ state === 'saving' ? $t('Saving…') : (maintenanceRecord ? $t('Update') : $t('Create')) }}
          </Button>
        </DialogFooter>
      </form>
    </DialogScrollContent>
  </Dialog>
</template>